/**
 * API client for the Photo Configuration page. Two surfaces share this
 * file because they share endpoints:
 *
 *   1. The global Reference Library — every image type (engine_bay,
 *      passenger_rear_corner_exterior, trunk_shot, ...) and its
 *      per-body-style reference images. Drives CLIP classification.
 *   2. Per-bucket configurations — for each vehicle-type bucket
 *      (truck, suv, sedan_coupe, rv, camper_trailer), the ordered set
 *      of image types from the library plus capture_order and
 *      publish_order.
 *
 * Response shapes mirror backend/schemas.py — keep them in lockstep.
 */
import { API } from "../config/api"

const TOKEN_KEY = "invi_token"

function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? ""
}

async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `HTTP ${res.status}`)
  }
  return res
}

async function authFetchMultipart(url: string, formData: FormData): Promise<Response> {
  const res = await fetch(url, {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `HTTP ${res.status}`)
  }
  return res
}

// --- Body styles ---

// Operator-facing tag on each reference image — describes what body
// style the photo depicts. NOT a filter on the matcher; every reference
// competes equally. More granular than vehicle-type buckets (sedan vs.
// coupe, camper vs. trailer) so operators can label references precisely.
export type BodyStyle =
  | "truck"
  | "suv"
  | "sedan"
  | "coupe"
  | "rv"
  | "camper"
  | "trailer"

export const ALL_BODY_STYLES: BodyStyle[] = [
  "truck", "suv", "sedan", "coupe", "rv", "camper", "trailer",
]

export const BODY_STYLE_DISPLAY_NAMES: Record<BodyStyle, string> = {
  truck: "Truck",
  suv: "SUV",
  sedan: "Sedan",
  coupe: "Coupe",
  rv: "RV",
  camper: "Camper",
  trailer: "Trailer",
}

// --- Response shapes (mirror backend/schemas.py) ---

export interface ImageTypeReferenceResponse {
  id: number
  body_style: BodyStyle
  reference_url: string
  uploaded_at: string
}

export interface VehicleTypeUsage {
  vehicle_type: string
  display_name: string
}

export interface ImageType {
  id: number
  slug: string
  display_name: string
  references: ImageTypeReferenceResponse[]
  vehicle_types_in_use: VehicleTypeUsage[]
}

export interface ImageTypeLibrary {
  image_types: ImageType[]
}

export interface CreateImageTypeRequest {
  display_name: string
  slug?: string | null
}

export interface UpdateImageTypeRequest {
  display_name: string
}

export interface ImageTypeReferenceUploadResponse {
  id: number
  image_type_id: number
  body_style: BodyStyle
  reference_url: string
  uploaded_at: string
}

export interface PhotoConfigurationShot {
  id: number
  image_type_id: number
  slug: string
  display_name: string
  capture_order: number
  publish_order: number
  /** Starting zoom the capture app applies for this shot; null = 1.0x. */
  default_zoom: number | null
  has_any_reference: boolean
  reference_count: number
  /** Distinct body styles with at least one reference — a body style can hold several. */
  body_styles_covered: number
}

export interface PhotoConfigurationShotList {
  vehicle_type: string
  display_name: string
  configured: boolean
  shots: PhotoConfigurationShot[]
}

export interface AddImageTypeToBucketRequest {
  image_type_id: number
}

export interface UpdateBucketShotRequest {
  /** Explicit null clears the shot back to neutral 1.0x. */
  default_zoom: number | null
}

export interface PhotoConfigurationReorderEntry {
  shot_id: number
  position: number
}

export type PhotoConfigurationReorderField = "capture_order" | "publish_order"

export interface PhotoConfigurationReorderRequest {
  field: PhotoConfigurationReorderField
  entries: PhotoConfigurationReorderEntry[]
}

export interface SeedCanonicalShotsResponse {
  vehicle_type: string
  image_types_created: number
  bucket_shots_created: number
  shots_total: number
}

export interface PhotoConfigurationClassifyMatch {
  slug: string
  display_name: string
  similarity: number
  matched_body_style: BodyStyle
}

export interface PhotoConfigurationClassifyResponse {
  matches: PhotoConfigurationClassifyMatch[]
  best_match_slug: string | null
  best_match_confidence: number | null
  threshold: number
  matched: boolean
}

// --- Library fetchers ---

export async function listImageTypes(): Promise<ImageTypeLibrary> {
  const url = `${API.baseUrl}${API.endpoints.photoConfiguration.imageTypes}`
  const res = await authFetch(url)
  return res.json()
}

export async function createImageType(
  payload: CreateImageTypeRequest,
): Promise<ImageType> {
  const url = `${API.baseUrl}${API.endpoints.photoConfiguration.imageTypes}`
  const res = await authFetch(url, { method: "POST", body: JSON.stringify(payload) })
  return res.json()
}

export async function updateImageType(
  imageTypeId: number,
  payload: UpdateImageTypeRequest,
): Promise<ImageType> {
  const url = `${API.baseUrl}${API.endpoints.photoConfiguration.imageType(imageTypeId)}`
  const res = await authFetch(url, { method: "PATCH", body: JSON.stringify(payload) })
  return res.json()
}

export async function deleteImageType(imageTypeId: number): Promise<void> {
  const url = `${API.baseUrl}${API.endpoints.photoConfiguration.imageType(imageTypeId)}`
  await authFetch(url, { method: "DELETE" })
}

export async function uploadImageTypeReference(
  imageTypeId: number,
  bodyStyle: BodyStyle,
  file: File,
): Promise<ImageTypeReferenceUploadResponse> {
  const formData = new FormData()
  formData.append("file", file)
  formData.append("body_style", bodyStyle)
  const url = `${API.baseUrl}${API.endpoints.photoConfiguration.reference(imageTypeId)}`
  const res = await authFetchMultipart(url, formData)
  return res.json()
}

// By id, not body style: a body style can hold several references.
export async function deleteImageTypeReference(
  imageTypeId: number,
  referenceId: number,
): Promise<void> {
  const url = `${API.baseUrl}${
    API.endpoints.photoConfiguration.referenceById(imageTypeId, referenceId)
  }`
  await authFetch(url, { method: "DELETE" })
}

// --- Bucket fetchers ---
//
// Shot lists are per dealer group: every bucket endpoint carries the
// active group as a `dealer_group_id` query param. The server scopes the
// request to that group (operator) or forces the caller's own group
// (dealership user); the per-dealer Configuration page only ever calls
// these with a concrete group selected.

function withGroup(path: string, dealerGroupId: number): string {
  const separator = path.includes("?") ? "&" : "?"
  return `${path}${separator}dealer_group_id=${dealerGroupId}`
}

export async function getShotList(
  vehicleType: string,
  dealerGroupId: number,
): Promise<PhotoConfigurationShotList> {
  const url = `${API.baseUrl}${
    withGroup(API.endpoints.photoConfiguration.shotList(vehicleType), dealerGroupId)
  }`
  const res = await authFetch(url)
  return res.json()
}

export async function addImageTypeToBucket(
  vehicleType: string,
  dealerGroupId: number,
  payload: AddImageTypeToBucketRequest,
): Promise<PhotoConfigurationShot> {
  const url = `${API.baseUrl}${
    withGroup(API.endpoints.photoConfiguration.bucketShots(vehicleType), dealerGroupId)
  }`
  const res = await authFetch(url, { method: "POST", body: JSON.stringify(payload) })
  return res.json()
}

export async function updateBucketShot(
  vehicleType: string,
  shotId: number,
  dealerGroupId: number,
  payload: UpdateBucketShotRequest,
): Promise<PhotoConfigurationShot> {
  const url = `${API.baseUrl}${
    withGroup(API.endpoints.photoConfiguration.bucketShot(vehicleType, shotId), dealerGroupId)
  }`
  const res = await authFetch(url, { method: "PATCH", body: JSON.stringify(payload) })
  return res.json()
}

export async function removeImageTypeFromBucket(
  vehicleType: string,
  shotId: number,
  dealerGroupId: number,
): Promise<void> {
  const url = `${API.baseUrl}${
    withGroup(API.endpoints.photoConfiguration.bucketShot(vehicleType, shotId), dealerGroupId)
  }`
  await authFetch(url, { method: "DELETE" })
}

export async function seedCanonicalShots(
  vehicleType: string,
  dealerGroupId: number,
): Promise<SeedCanonicalShotsResponse> {
  const url = `${API.baseUrl}${
    withGroup(API.endpoints.photoConfiguration.seedCanonical(vehicleType), dealerGroupId)
  }`
  const res = await authFetch(url, { method: "POST" })
  return res.json()
}

export async function reorderShots(
  vehicleType: string,
  dealerGroupId: number,
  payload: PhotoConfigurationReorderRequest,
): Promise<PhotoConfigurationShotList> {
  const url = `${API.baseUrl}${
    withGroup(API.endpoints.photoConfiguration.reorder(vehicleType), dealerGroupId)
  }`
  const res = await authFetch(url, { method: "POST", body: JSON.stringify(payload) })
  return res.json()
}

// --- Test classification ---

export async function classifyTestPhoto(
  file: File,
): Promise<PhotoConfigurationClassifyResponse> {
  const formData = new FormData()
  formData.append("file", file)
  const url = `${API.baseUrl}${API.endpoints.photoConfiguration.classify}`
  const res = await authFetchMultipart(url, formData)
  return res.json()
}
